import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import renderMentions from '../lib/renderMentions'
import { decodeEntities } from '../lib/decodeEntities'
import { supabase } from '../lib/supabase'
import ReportModal from './ReportModal'

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  return session ? { Authorization: `Bearer ${session.access_token}` } : {}
}

export function wallTimeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const C = {
  dropBg: 'var(--surface)',
  dropBorder: 'var(--border)',
  editBg: 'var(--surface-secondary)',
  editBorder: 'var(--border)',
  textFaint: 'var(--text-faint)',
}

function DropdownMenu({ onEdit, onDelete, onClose }) {
  const ref = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  return (
    <div ref={ref} style={{
      position: 'absolute', right: 0, top: '100%', zIndex: 10,
      background: C.dropBg, color: 'var(--text)', border: `1px solid ${C.dropBorder}`,
      borderRadius: 6, overflow: 'hidden', minWidth: 100,
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    }}>
      <button onClick={onEdit} style={{
        display: 'block', width: '100%', padding: '8px 14px', border: 'none',
        background: 'none', color: 'inherit', fontSize: '0.85rem', textAlign: 'left', cursor: 'pointer',
      }}>Edit</button>
      <button onClick={onDelete} style={{
        display: 'block', width: '100%', padding: '8px 14px', border: 'none',
        background: 'none', color: '#ef4444', fontSize: '0.85rem', textAlign: 'left', cursor: 'pointer',
      }}>Delete</button>
    </div>
  )
}

export function WallPost({ post, liked, onToggleLike, onDelete, onEdit, canDelete, user, isAdmin, onCommentsCountChange, focusCommentId = null, focused = false }) {
  const [commentsOpen, setCommentsOpen] = useState(false)

  useEffect(() => {
    if (focusCommentId) setCommentsOpen(true)
  }, [focusCommentId])

  const [commentCount, setCommentCount] = useState(post.comments_count ?? 0)
  const [reportOpen, setReportOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editContent, setEditContent] = useState('')
  const [saving, setSaving] = useState(false)

  function handleCountChange(delta) {
    setCommentCount(prev => Math.max(0, prev + delta))
    if (onCommentsCountChange) onCommentsCountChange(delta)
  }

  async function handleSaveEdit() {
    const trimmed = editContent.trim()
    if (!trimmed) return
    setSaving(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.put(`/api/wall/posts/${post.id}`, { content: trimmed }, { headers })
      if (onEdit) onEdit(post.id, data)
      setEditing(false)
    } catch { /* empty */ }
    finally { setSaving(false) }
  }

  const isEdited = post.updated_at && post.updated_at !== post.created_at
  const avatarStyle = { background: post.authorAvatarUrl ? undefined : (post.authorAvatarGrad ?? 'linear-gradient(135deg, #7C3AED, #EC4899)') }

  return (
    <div id={`wall-post-${post.id}`} className={`wall-post${focused ? ' wall-post--focused' : ''}`}>
      <div className="wall-post__header">
        <Link to={`/user/${post.authorUsername}`} className="wall-post__avatar" style={avatarStyle}>
          {post.authorAvatarUrl
            ? <img src={post.authorAvatarUrl} alt={post.authorDisplayName ?? post.authorUsername} />
            : (post.authorDisplayName ?? post.authorUsername)?.[0]?.toUpperCase() ?? '?'}
        </Link>
        <div className="wall-post__meta">
          <Link to={`/user/${post.authorUsername}`} className="wall-post__author">
            {decodeEntities(post.authorDisplayName ?? post.authorUsername)}
          </Link>
          <div className="wall-post__time">
            {wallTimeAgo(post.created_at)}
            {isEdited && <span style={{ marginLeft: 4, color: C.textFaint, fontSize: '0.8em' }}>(edited)</span>}
          </div>
        </div>
        {user && user.id !== post.user_id && (
          <button className="wall-post__report" onClick={() => setReportOpen(true)} title="Report" aria-label="Report">⚑</button>
        )}
        {canDelete && (
          <div style={{ position: 'relative' }}>
            <button
              className="wall-post__delete"
              onClick={() => setMenuOpen(m => !m)}
              aria-label="Post actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title="More"
            >⋯</button>
            {menuOpen && (
              <DropdownMenu
                onEdit={() => { setEditing(true); setEditContent(post.content); setMenuOpen(false) }}
                onDelete={() => { onDelete(); setMenuOpen(false) }}
                onClose={() => setMenuOpen(false)}
              />
            )}
          </div>
        )}
      </div>
      {editing ? (
        <div style={{ padding: '8px 0' }}>
          <textarea
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            maxLength={5000}
            rows={3}
            style={{
              width: '100%', padding: 10, borderRadius: 6, border: `1px solid ${C.editBorder}`,
              background: C.editBg, color: 'inherit', fontSize: '0.9rem', resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '5px 14px' }} onClick={handleSaveEdit} disabled={saving || !editContent.trim()}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="btn" style={{ fontSize: '0.8rem', padding: '5px 14px', background: 'none', border: `1px solid ${C.editBorder}`, color: 'inherit', cursor: 'pointer', borderRadius: 6 }} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="wall-post__content">{renderMentions(post.content)}</div>
      )}
      <div className="wall-post__actions">
        <button
          className={`wall-post__like${liked ? ' wall-post__like--active' : ''}`}
          onClick={(e) => { e.preventDefault(); onToggleLike() }}
        >
          {liked ? '❤️' : '🤍'} {post.likes_count ?? 0}
        </button>
        <button
          className={`wall-post__comment-toggle${commentsOpen ? ' wall-post__comment-toggle--active' : ''}`}
          onClick={() => setCommentsOpen(prev => !prev)}
        >
          💬 {commentCount}
        </button>
      </div>
      {reportOpen && <ReportModal contentType="wall_post" contentId={post.id} onClose={() => setReportOpen(false)} />}
      {commentsOpen && (
        <WallComments postId={post.id} user={user} isAdmin={isAdmin} onCountChange={handleCountChange} focusCommentId={focusCommentId} />
      )}
    </div>
  )
}

export function WallComments({ postId, user, isAdmin, onCountChange, focusCommentId = null }) {
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [posting, setPosting] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null)
  const [replyText, setReplyText] = useState('')
  const [replyPosting, setReplyPosting] = useState(false)
  const [likedComments, setLikedComments] = useState(new Set())
  const [expanded, setExpanded] = useState(new Set())
  const [reportingCommentId, setReportingCommentId] = useState(null)
  const [editingCommentId, setEditingCommentId] = useState(null)
  const [editCommentContent, setEditCommentContent] = useState('')
  const [editCommentSaving, setEditCommentSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const headers = await getHeaders()
        const { data } = await axios.get(`/api/wall/posts/${postId}/comments`, { headers })
        if (!cancelled) {
          setComments(data)
          setLikedComments(new Set((data ?? []).filter(c => c.likedByMe).map(c => c.id)))
        }
      } catch { /* empty */ }
      if (!cancelled) setLoading(false)
    })()
    return () => { cancelled = true }
  }, [postId])

  async function handleComment(e) {
    e.preventDefault()
    const content = text.trim()
    if (!content) return
    setPosting(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.post(`/api/wall/posts/${postId}/comments`, { content }, { headers })
      setComments(prev => [...prev, data])
      setText('')
      onCountChange(1)
    } catch { /* empty */ }
    setPosting(false)
  }

  async function handleReply(parentId) {
    const content = replyText.trim()
    if (!content) return
    setReplyPosting(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.post(`/api/wall/posts/${postId}/comments`, { content, parent_comment_id: parentId }, { headers })
      setComments(prev => [...prev, data])
      setReplyText('')
      setReplyingTo(null)
      onCountChange(1)
    } catch { /* empty */ }
    setReplyPosting(false)
  }

  async function handleDelete(commentId) {
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/wall/comments/${commentId}`, { headers })
      setComments(prev => prev.filter(c => c.id !== commentId))
      onCountChange(-1)
    } catch { /* empty */ }
  }

  async function handleSaveCommentEdit(commentId) {
    const trimmed = editCommentContent.trim()
    if (!trimmed) return
    setEditCommentSaving(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.put(`/api/wall/comments/${commentId}`, { content: trimmed }, { headers })
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, content: data.content, updated_at: data.updated_at } : c))
      setEditingCommentId(null)
    } catch { /* empty */ }
    finally { setEditCommentSaving(false) }
  }

  async function handleToggleLike(commentId) {
    const wasLiked = likedComments.has(commentId)
    setLikedComments(prev => { const s = new Set(prev); wasLiked ? s.delete(commentId) : s.add(commentId); return s })
    setComments(prev => prev.map(c => c.id === commentId ? { ...c, likes_count: c.likes_count + (wasLiked ? -1 : 1) } : c))
    try {
      const headers = await getHeaders()
      const { data } = await axios.post(`/api/wall/comments/${commentId}/like`, {}, { headers })
      setLikedComments(prev => { const s = new Set(prev); data.liked ? s.add(commentId) : s.delete(commentId); return s })
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, likes_count: data.likes_count } : c))
    } catch {
      setLikedComments(prev => { const s = new Set(prev); wasLiked ? s.add(commentId) : s.delete(commentId); return s })
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, likes_count: c.likes_count + (wasLiked ? 1 : -1) } : c))
    }
  }

  // Scroll to + flash-highlight the focused comment once data loads.
  useEffect(() => {
    if (!focusCommentId || loading) return
    const target = comments.find(c => c.id === focusCommentId)
    if (!target) return
    if (target.parent_comment_id) {
      setExpanded(prev => prev.has(target.parent_comment_id) ? prev : new Set(prev).add(target.parent_comment_id))
    }
    const raf = requestAnimationFrame(() => {
      document.getElementById(`wall-comment-${focusCommentId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
    return () => cancelAnimationFrame(raf)
  }, [focusCommentId, loading, comments])

  const topLevel = comments.filter(c => !c.parent_comment_id)
  const focusMissing = focusCommentId && !loading && !comments.some(c => c.id === focusCommentId)

  function renderComment(c, isReply = false, rootId = null) {
    const replies = comments.filter(r => r.parent_comment_id === c.id)
    const isExpanded = expanded.has(c.id)
    const hiddenCount = replies.length > 2 && !isExpanded ? replies.length - 2 : 0
    const visibleReplies = isExpanded ? replies : replies.slice(-2)
    const avatarStyle = { background: c.authorAvatarUrl ? undefined : (c.authorAvatarGrad ?? 'linear-gradient(135deg, #7C3AED, #EC4899)') }
    const canDel = user?.id === c.user_id || isAdmin
    const isEdited = c.updated_at && c.updated_at !== c.created_at
    const isEditingThis = editingCommentId === c.id

    return (
      <div key={c.id} id={`wall-comment-${c.id}`} className={`wall-comment${isReply ? ' wall-comment--reply' : ''}${focusCommentId === c.id ? ' wall-comment--focused' : ''}`}>
        <Link to={`/user/${c.authorUsername}`} className="wall-comment__avatar" style={avatarStyle}>
          {c.authorAvatarUrl
            ? <img src={c.authorAvatarUrl} alt={c.authorDisplayName ?? c.authorUsername} />
            : (c.authorDisplayName ?? c.authorUsername)?.[0]?.toUpperCase() ?? '?'}
        </Link>
        <div className="wall-comment__body">
          <div className="wall-comment__header">
            <Link to={`/user/${c.authorUsername}`} className="wall-comment__author">{c.authorDisplayName ?? c.authorUsername}</Link>
            <span className="wall-comment__time">
              {wallTimeAgo(c.created_at)}
              {isEdited && <span style={{ marginLeft: 3, color: C.textFaint, fontSize: '0.8em' }}>(edited)</span>}
            </span>
            {user && user.id !== c.user_id && (
              <button className="wall-post__report" style={{ marginLeft: canDel ? 0 : 'auto' }} onClick={() => setReportingCommentId(c.id)} title="Report" aria-label="Report">⚑</button>
            )}
            {canDel && (
              <div style={{ position: 'relative', marginLeft: user && user.id !== c.user_id ? 0 : 'auto' }}>
                <button
                  className="wall-comment__delete"
                  onClick={() => setEditingCommentId(editingCommentId === `menu-${c.id}` ? null : `menu-${c.id}`)}
                  aria-label="Comment actions"
                  aria-haspopup="menu"
                  aria-expanded={editingCommentId === `menu-${c.id}`}
                  title="More"
                >⋯</button>
                {editingCommentId === `menu-${c.id}` && (
                  <DropdownMenu
                    onEdit={() => { setEditingCommentId(c.id); setEditCommentContent(c.content) }}
                    onDelete={() => { handleDelete(c.id); setEditingCommentId(null) }}
                    onClose={() => setEditingCommentId(null)}
                  />
                )}
              </div>
            )}
          </div>
          {isEditingThis ? (
            <div style={{ padding: '4px 0' }}>
              <textarea
                value={editCommentContent}
                onChange={e => setEditCommentContent(e.target.value)}
                maxLength={2000}
                rows={2}
                style={{
                  width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${C.editBorder}`,
                  background: C.editBg, color: 'inherit', fontSize: '0.85rem', resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <button className="btn btn-primary" style={{ fontSize: '0.78rem', padding: '4px 12px' }} onClick={() => handleSaveCommentEdit(c.id)} disabled={editCommentSaving || !editCommentContent.trim()}>
                  {editCommentSaving ? '...' : 'Save'}
                </button>
                <button style={{ fontSize: '0.78rem', padding: '4px 12px', background: 'none', border: `1px solid ${C.editBorder}`, color: 'inherit', cursor: 'pointer', borderRadius: 6 }} onClick={() => setEditingCommentId(null)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="wall-comment__text">{renderMentions(c.content)}</div>
          )}
          <div className="wall-comment__actions">
            {user && (
              <button className="wall-comment__reply-btn" onClick={() => { setReplyingTo(replyingTo === c.id ? null : c.id); setReplyText('') }}>
                Reply
              </button>
            )}
            <button
              className={`wall-comment__like${likedComments.has(c.id) ? ' wall-comment__like--active' : ''}`}
              onClick={() => handleToggleLike(c.id)}
            >
              {likedComments.has(c.id) ? '❤️' : '♡'} {c.likes_count ?? 0}
            </button>
          </div>
          {reportingCommentId === c.id && (
            <ReportModal contentType="wall_comment" contentId={c.id} onClose={() => setReportingCommentId(null)} />
          )}
          {replyingTo === c.id && (
            <div className="wall-comment__reply-form">
              <input
                className="wall-comment-form__input"
                placeholder="Write a reply…"
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                maxLength={2000}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-primary" style={{ fontSize: '0.78rem', padding: '5px 12px' }} onClick={() => handleReply(rootId ?? c.id)} disabled={replyPosting || !replyText.trim()}>
                  {replyPosting ? '…' : 'Reply'}
                </button>
                <button className="wall-comment__reply-cancel" onClick={() => setReplyingTo(null)}>Cancel</button>
              </div>
            </div>
          )}
          {!isReply && replies.length > 0 && (
            <div className="wall-comment__replies">
              {hiddenCount > 0 && (
                <button className="wall-comment__show-more" onClick={() => setExpanded(prev => new Set(prev).add(c.id))}>
                  + Show {hiddenCount} more {hiddenCount === 1 ? 'reply' : 'replies'}
                </button>
              )}
              {visibleReplies.map(r => renderComment(r, true, c.id))}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (loading) return <div className="wall-comments"><div className="wall-skeleton" style={{ height: 60 }} /></div>

  return (
    <div className="wall-comments">
      {user && (
        <form className="wall-comment-form" onSubmit={handleComment}>
          <input
            className="wall-comment-form__input"
            placeholder="Write a comment…"
            value={text}
            onChange={e => setText(e.target.value)}
            maxLength={2000}
          />
          <button className="btn btn-primary" type="submit" style={{ fontSize: '0.78rem', padding: '5px 12px', whiteSpace: 'nowrap' }} disabled={posting || !text.trim()}>
            {posting ? '…' : 'Post'}
          </button>
        </form>
      )}
      {topLevel.length === 0 && !loading && (
        <div style={{ padding: '12px 0', color: 'var(--text-faint)', fontSize: '0.85rem' }}>No comments yet.</div>
      )}
      {focusMissing && (
        <div style={{ padding: '8px 0', color: 'var(--text-faint)', fontSize: '0.85rem' }}>Linked comment is unavailable.</div>
      )}
      {topLevel.map(c => renderComment(c))}
    </div>
  )
}
